import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

type ColorModeContextType = {
  mode: 'light' | 'dark';
  toggleColorMode: () => void;
};

export const ColorModeContext = React.createContext<ColorModeContextType>({
  mode: 'dark',
  toggleColorMode: () => {},
});

export const ColorModeProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const STORAGE_KEY = 'racs-color-mode';
  const [mode, setMode] = React.useState<'light' | 'dark'>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') return stored;
    } catch (e) {
      // ignore
    }
    return 'dark';
  });

  const colorMode = React.useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setMode((prev) => {
          const next = prev === 'light' ? 'dark' : 'light';
          try {
            localStorage.setItem(STORAGE_KEY, next);
          } catch (e) {}
          return next;
        });
      },
    }),
    [mode]
  );

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          background: {
            default: mode === 'dark' ? '#0b0b0b' : '#f5f5f5',
            paper: mode === 'dark' ? '#101010' : '#ffffff',
          },
          primary: {
            main: '#1976d2',
          },
        },
        components: {
          MuiPaper: {
            defaultProps: {
              elevation: 0,
            },
          },
        },
      }),
    [mode]
  );

  // ensure persisted value reflected if localStorage changed elsewhere
  React.useEffect(() => {
    const handler = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'light' || stored === 'dark') setMode(stored);
      } catch (e) {}
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default ColorModeProvider;
